import { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  openTaskCount,
  taskEmployees,
  visibleTasks,
  type TaskEmployeeLike,
  type TaskLike,
} from '@agrogruda/shared';
import { useAuth } from '@/auth/useAuth';
import { supabase } from '@/lib/supabase';
import { TASKS_KEY, TASK_EMPLOYEES_KEY, fetchTaskEmployees, fetchTasks, toTaskLike } from './api';

const NO_TASKS: TaskLike[] = [];
const NO_EMPLOYEES: TaskEmployeeLike[] = [];

/** Marks the task list stale (after every task RPC and on realtime events). */
export function invalidateTasks(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: TASKS_KEY });
}

export interface TasksData {
  /** Every task the database lets this login read (RLS), newest first. */
  all: TaskLike[];
  /** visibleTasks(): the director and the staff see every task. */
  visible: TaskLike[];
  /** Prototype taskEmployees(): sorted with the sq collation. */
  employees: TaskEmployeeLike[];
  isAdmin: boolean;
  loading: boolean;
  error: string | null;
}

export function useTasksData(enabled = true): TasksData {
  const { role } = useAuth();
  const tasks = useQuery({ queryKey: TASKS_KEY, queryFn: fetchTasks, enabled });
  const employees = useQuery({ queryKey: TASK_EMPLOYEES_KEY, queryFn: fetchTaskEmployees, enabled });

  const all = useMemo(() => (tasks.data ?? []).map(toTaskLike), [tasks.data]);
  const employeeList = useMemo(() => taskEmployees(employees.data ?? NO_EMPLOYEES), [employees.data]);
  const visible = useMemo(() => (role ? visibleTasks(all, role) : NO_TASKS), [all, role]);
  const error = tasks.error ?? employees.error;
  return {
    all,
    visible,
    employees: employeeList,
    isAdmin: role === 'admin',
    loading: tasks.isPending || employees.isPending,
    error: error instanceof Error ? error.message : null,
  };
}

/** Prototype updateTaskNavBadge count (open visible tasks); 0 while loading. */
export function useOpenTaskCount(): number {
  const { visible } = useTasksData();
  return openTaskCount(visible);
}

/**
 * Realtime `postgres_changes` on `tasks` → the list is refetched (ARCHITECTURE §3.2). Mount once per app
 * (TasksSessionEffects); progress changes update the tasks row too, so the history arrives with the refetch.
 */
export function useTasksRealtime(enabled = true): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!enabled) return;
    const channel = supabase
      .channel('tasks-live')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks' }, () => {
        void invalidateTasks(queryClient);
      })
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [enabled, queryClient]);
}
